import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate, sortByParam } from '@/services/http/mock'
import type { PaginatedData } from '@/types/api'
import type {
  GroupBindingGenerateProjectPayload,
  GroupBindingItem,
  GroupBindingListParams,
  ManualGroupCreatePayload,
  ManualGroupCreateResult,
  GroupBindingProjectNameSuggestionPayload,
  GroupBindingProjectNameSuggestionResult,
  GroupBindingProductRecommendationPayload,
  GroupBindingProductRecommendationResult,
  GroupBindingProjectCreateResult,
  GroupBindingUpdatePayload,
  ZentaoMappingOptions,
  ZentaoMappingOptionsSyncResult,
} from '@/types/groupBinding'

const mockBindings: GroupBindingItem[] = [
  {
    id: 15,
    room_id: 'wrE5WZJAAADcCcj3L0wJkZDZltiDo56w',
    room_name: '栟茶古镇项目交流群',
    group_source: 'wechat',
    customer_name: '栟茶古镇',
    project_name: '栟茶古镇',
    service_owner: 'ops_admin',
    sales_owner: 'sales_a',
    tech_owner: 'wen-da-bot',
    zentao_product_id: 4,
    zentao_project_id: 6,
    zentao_project_name: 'QCS2.0产品迭代',
    zentao_project_status: 'doing',
    zentao_execution_id: 7,
    default_execution_id: 7,
    execution_routing_mode: 'auto',
    zentao_execution_name: '2.4.4',
    zentao_execution_status: 'doing',
    customer_level: 'A',
    business_stage: '售后',
    remark: '待人工校准',
    is_active: true,
    validated: true,
    created_at: '2026-05-22T04:30:00Z',
    updated_at: '2026-05-22T04:31:16Z',
  },
]

function filterMockBindings(params: GroupBindingListParams) {
  const keyword = (params.keyword || '').trim()
  const roomId = (params.room_id || '').trim()
  const serviceOwnerValue = (params.service_owner || '').trim()
  return mockBindings.filter((item) => {
    const matchesKeyword = !keyword || [item.room_name, item.customer_name, item.project_name, item.zentao_project_name, item.zentao_execution_name]
      .some((value) => value.includes(keyword))
    const matchesRoomId = !roomId || item.room_id.includes(roomId)
    const matchesServiceOwner = !serviceOwnerValue || item.service_owner === serviceOwnerValue
    const matchesActive = params.is_active === undefined || item.is_active === params.is_active
    return matchesKeyword && matchesRoomId && matchesServiceOwner && matchesActive
  })
}

export async function createManualGroup(payload: ManualGroupCreatePayload): Promise<ManualGroupCreateResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const now = new Date().toISOString()
    const item: GroupBindingItem = {
      id: Math.max(0, ...mockBindings.map((binding) => binding.id)) + 1,
      room_id: `manual_${Date.now()}`,
      room_name: payload.room_name.trim(),
      group_source: 'manual',
      customer_name: '待补充',
      project_name: '待补充',
      service_owner: 'ops_admin',
      tech_owner: 'unassigned',
      zentao_product_id: 0,
      zentao_project_id: 0,
      zentao_project_name: '',
      zentao_project_status: '',
      zentao_execution_id: 0,
      default_execution_id: 0,
      execution_routing_mode: 'auto',
      zentao_execution_name: '',
      zentao_execution_status: '',
      customer_level: 'pending',
      business_stage: 'pending',
      remark: '手工维护群',
      is_active: false,
      validated: false,
      created_at: now,
      updated_at: now,
    }
    mockBindings.unshift(item)
    return {
      id: item.id,
      room_id: item.room_id,
      room_name: item.room_name,
      group_source: 'manual',
      is_active: false,
      validated: false,
      created_at: now,
    }
  }

  const { data } = await client.post<ManualGroupCreateResult>('/group-bindings/manual', payload)
  return data
}

export async function fetchGroupBindings(params: GroupBindingListParams): Promise<PaginatedData<GroupBindingItem>> {
  if (isMockEnabled()) {
    await mockDelay()
    const page = params.page ?? 1
    const pageSize = params.page_size ?? 20
    const filtered = sortByParam(filterMockBindings(params), params.sort_by, params.sort_order, {
      created_at: 'created_at',
      updated_at: 'updated_at',
      room_name: 'room_name',
      customer_name: 'customer_name',
    })
    return paginate(filtered, page, pageSize)
  }

  const { data } = await client.get<PaginatedData<GroupBindingItem>>('/group-bindings', { params })
  return data
}

export async function exportGroupBindings(params: Omit<GroupBindingListParams, 'page' | 'page_size'>): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const blob = new Blob(['\ufeff', buildMockExportCsv(filterMockBindings(params))], { type: 'text/csv;charset=utf-8' })
    downloadBlob(blob, `group-bindings-${new Date().toISOString().slice(0, 10)}.csv`)
    return
  }

  const response = await client.get<Blob>('/group-bindings/export', {
    params,
    responseType: 'blob',
  })
  const contentDisposition = response.headers['content-disposition']
  downloadBlob(
    response.data,
    extractDownloadFilename(typeof contentDisposition === 'string' ? contentDisposition : undefined),
  )
}

function buildMockExportCsv(items: GroupBindingItem[]) {
  const headers = ['ID', '群ID', '群名', '群来源', '接待负责人', '默认首派研发', '客户名', '项目名', '销售负责人', '禅道产品ID', '禅道项目ID', '禅道项目名', '默认执行ID', '默认执行名', '启用', '已验证', '备注', '更新时间']
  const rows = items.map((item) => [
    item.id,
    item.room_id,
    item.room_name,
    item.group_source === 'manual' ? '手工群' : '企业微信群',
    item.service_owner,
    item.tech_owner,
    item.customer_name,
    item.project_name,
    item.sales_owner || '',
    item.zentao_product_id,
    item.zentao_project_id,
    item.zentao_project_name,
    item.zentao_execution_id,
    item.zentao_execution_name,
    item.is_active ? '是' : '否',
    item.validated ? '是' : '否',
    item.remark || '',
    item.updated_at,
  ])
  return [headers, ...rows].map((row) => row.map(formatCsvCell).join(',')).join('\r\n')
}

function formatCsvCell(value: unknown) {
  const text = String(value ?? '')
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function extractDownloadFilename(contentDisposition?: string) {
  const encodedMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1])
  }
  const quotedMatch = contentDisposition?.match(/filename="([^"]+)"/i)
  return quotedMatch?.[1] || `group-bindings-${new Date().toISOString().slice(0, 10)}.xlsx`
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export async function updateGroupBinding(
  bindingId: number,
  payload: GroupBindingUpdatePayload,
): Promise<{ id: number; updated_at: string }> {
  if (isMockEnabled()) {
    await mockDelay()
    const item = mockBindings.find((binding) => binding.id === bindingId)
    if (!item) throw new Error('群映射不存在')
    Object.assign(item, payload, { updated_at: new Date().toISOString() })
    return { id: item.id, updated_at: item.updated_at }
  }

  const { data } = await client.put<{ id: number; updated_at: string }>(`/group-bindings/${bindingId}`, payload)
  return data
}

export async function fetchZentaoMappingOptions(projectId?: number): Promise<ZentaoMappingOptions> {
  if (isMockEnabled()) {
    await mockDelay()
    const projects = [
      { project_id: 6, name: 'QCS2.0产品迭代', status: 'doing', model: 'scrum' },
      { project_id: 101, name: '栟茶古镇', status: 'doing', model: 'scrum' },
    ]
    const executions = [
      { execution_id: 7, project_id: 6, name: '2.4.4', status: 'doing', begin: null, end: null },
      { execution_id: 202, project_id: 101, name: '售后专项', status: 'doing', begin: null, end: null },
    ]
    return {
      zentao_live_available: true,
      product_tree_source: 'live',
      product_tree: [
        { type: 'product', product_id: 4, name: '排队叫号2.0', status: 'normal', children: [] },
        { type: 'product', product_id: 67, name: '体检导检1.0', status: 'normal', children: [] },
      ],
      project_tree: projects.map((project) => ({
        type: 'project',
        project_id: project.project_id,
        name: project.name,
        status: project.status,
        children: executions
          .filter((execution) => execution.project_id === project.project_id)
          .map((execution) => ({
            type: 'execution',
            execution_id: execution.execution_id,
            project_id: execution.project_id,
            name: execution.name,
            status: execution.status,
          })),
      })),
      projects,
      executions: executions.filter((item) => projectId === undefined || item.project_id === projectId),
      service_owner_options: [
        { account: 'ops_admin', realname: '运营管理员', status: 'active', role: 'admin,service_agent', dept: '' },
      ],
      tech_owner_options: [
        { account: 'ops_admin', realname: '运营管理员', status: 'active', role: 'admin', dept: '' },
      ],
    }
  }

  const { data } = await client.get<ZentaoMappingOptions>('/group-bindings/zentao-options', {
    params: projectId === undefined ? undefined : { project_id: projectId },
  })
  return data
}

export async function syncZentaoMappingOptions(): Promise<ZentaoMappingOptionsSyncResult> {
  if (isMockEnabled()) {
    await mockDelay()
    return {
      entity_types: ['product', 'project', 'user'],
      sync_result: {
        product: { created: 0, updated: 2, skipped: 0, fetched: 2 },
        project: { created: 0, updated: 2, skipped: 0 },
        user: { created: 0, updated: 1, skipped: 0 },
        _summary: {
          total_created: 0,
          total_updated: 5,
          total_skipped: 0,
          dry_run: false,
          status: 'success',
        },
      },
    }
  }

  const { data } = await client.post<ZentaoMappingOptionsSyncResult>('/group-bindings/zentao-options/sync')
  return data
}

export async function suggestGroupBindingProjectName(
  payload: GroupBindingProjectNameSuggestionPayload,
): Promise<GroupBindingProjectNameSuggestionResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const candidate = (payload.customer_name || payload.room_name || payload.project_name || '')
      .replace(/售后维护群|售后群|项目群|交流群|工作群|群|项目/g, '')
      .trim()
    return {
      suggested_project_name: candidate,
      source: candidate ? 'mock' : '',
      changed: candidate !== (payload.project_name || '').trim(),
      original_errors: [],
      reason: candidate ? '' : '无法推荐项目名',
    }
  }

  const { data } = await client.post<GroupBindingProjectNameSuggestionResult>(
    '/group-bindings/project-name-suggestion',
    payload,
  )
  return data
}

export async function recommendGroupBindingProduct(params: {
  bindingId: number
  payload: GroupBindingProductRecommendationPayload
}): Promise<GroupBindingProductRecommendationResult> {
  if (isMockEnabled()) {
    await mockDelay()
    return {
      product_id: 4,
      product_name: '排队叫号2.0',
      source: 'ai',
      confidence: 0.92,
      reason: '群名称与产品名称匹配',
      fallback_used: false,
      warnings: [],
    }
  }

  const { data } = await client.post<GroupBindingProductRecommendationResult>(
    `/group-bindings/${params.bindingId}/product-recommendation`,
    params.payload,
  )
  return data
}

export async function generateZentaoProjectForBinding(params: {
  bindingId: number
  zentaoProductId: number
  currentValues?: Pick<GroupBindingUpdatePayload, 'room_name' | 'customer_name' | 'project_name'>
}): Promise<GroupBindingProjectCreateResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const item = mockBindings.find((binding) => binding.id === params.bindingId)
    if (!item) throw new Error('群映射不存在')
    Object.assign(item, params.currentValues || {})
    const projectId = Math.floor(90000 + Math.random() * 10000)
    item.zentao_product_id = params.zentaoProductId
    item.zentao_project_id = projectId
    item.zentao_project_name = item.project_name || item.customer_name || item.room_name
    item.zentao_project_status = 'doing'
    item.zentao_execution_id = 0
    item.default_execution_id = 0
    item.execution_routing_mode = 'auto'
    item.validated = true
    item.updated_at = new Date().toISOString()
    return {
      dry_run: false,
      batch: `web-${Date.now()}`,
      summary: { total: 1, project_created: 1, blocked: 0, already_bound: 0, failed: 0 },
      items: [{
        candidate_id: String(item.id),
        binding_id: item.id,
        room_id: item.room_id,
        room_name: item.room_name,
        status: 'project_created',
        reason: 'mock project created',
        zentao_project_id: projectId,
        zentao_product_id: params.zentaoProductId,
        zentao_execution_id: 0,
        execution_routing_mode: 'auto',
        project_created: true,
      }],
    }
  }

  const payload: GroupBindingGenerateProjectPayload = {
    zentao_product_id: params.zentaoProductId,
    room_name: params.currentValues?.room_name,
    customer_name: params.currentValues?.customer_name,
    project_name: params.currentValues?.project_name,
  }
  const { data } = await client.post<GroupBindingProjectCreateResult>(
    `/group-bindings/${params.bindingId}/generate-zentao-project`,
    payload,
  )
  return data
}
